// 销售应收管理页的纯工具函数与无状态展示片段。
// 从 SalesRevenueManagementPage.jsx 抽出，不依赖组件 state。

import React from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { RECEIVABLE_TYPE_TEXT } from "./salesRevenueConstants";

dayjs.extend(utc);

export const normalizeAirport = (value) => (value ? String(value).trim().toUpperCase() : "");
export const normalizeCurrency = (value) => (value ? String(value).trim().toUpperCase() : "USD");
export const normalizeStops = (values = []) =>
    values
        .map((item) => normalizeAirport(item))
        .filter(Boolean)
        .filter((item, index, array) => array.indexOf(item) === index);

export const utcToday = () => dayjs.utc().format("YYYY-MM-DD");

export const isContractPastEndDate = (record, today = utcToday()) =>
    record?.status === "ACTIVE" && Boolean(record?.end_date) && record.end_date < today;

export const formatDate = (value) => {
    if (!value) {
        return "—";
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : value;
};

export const formatDateTime = (value) => {
    if (!value) {
        return "—";
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : value;
};

export const formatUtcDate = (value) => {
    if (!value) {
        return "—";
    }
    const parsed = dayjs.utc(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : value;
};

export const formatUtcDateTime = (value) => {
    if (!value) {
        return "—";
    }
    const parsed = dayjs.utc(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : value;
};

export const sortFlightLegs = (legs) => {
    if (!Array.isArray(legs) || legs.length === 0) {
        return [];
    }
    return [...legs].sort((a, b) => {
        const ai = a?.rotation_leg_index ?? 0;
        const bi = b?.rotation_leg_index ?? 0;
        if (ai !== bi) {
            return ai - bi;
        }
        const at = a?.std ? dayjs(a.std).valueOf() : 0;
        const bt = b?.std ? dayjs(b.std).valueOf() : 0;
        return at - bt;
    });
};

export const formatLegSegment = (leg) => `${leg?.departure_airport || "—"} → ${leg?.arrival_airport || "—"}`;

export const formatMoney = (value) => {
    if (value == null || value === "") {
        return "—";
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return value;
    }
    return numeric.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

export const formatRange = (startDate, endDate) => `${formatDate(startDate)} ~ ${formatDate(endDate)}`;

export const formatPercent = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return "0%";
    }
    return `${Math.max(0, Math.min(100, Math.round(numeric)))}%`;
};

// 后端业务错误码 → 中文提示。只收录本页用户高频触达、裸错误码无法自解释的码。
const SALES_ERROR_MESSAGE_MAP = {
    SALES_CONTRACT_NO_DUPLICATE: "合同编号已被使用：同组织内已存在相同编号且未终止/未过期的合同",
    SALES_CONTRACT_NO_IMMUTABLE: "已终止/已过期的合同不能修改编号",
    SALES_CONTRACT_NO_ACTIVE_ROUTE_PRICE: "至少要有一条启用中的航线价格，才能把合同设为生效",
    SALES_CONTRACT_TERMINATED_FINAL: "已终止的合同不能再启用或改成其他状态",
    SALES_CONTRACT_CREATE_DRAFT_REQUIRED: "新建合同只能先保存为草稿，配好航线价格后再启用",
    SALES_CONTRACT_STATUS_TRANSITION: "当前状态不能改成这个状态。生效中的合同不能退回草稿",
    SALES_CONTRACT_DATE_INVALID: "失效日期不能早于生效日期",
    SALES_CONTRACT_DATE_REQUIRED: "请填写生效日期和失效日期",
    SALES_CONTRACT_BASE_FUEL_PRICE_REQUIRED: "生成燃油时还没有基准油价。按段油耗的基准价按机场，不要求填在合同头上",
    SALES_CONTRACT_KIND_IMMUTABLE: "合同类型创建后不能修改",
    SALES_ROUTE_PRICE_OVERLAP: "同一航线、同一机型的价格有效期重叠",
    SALES_ROUTE_PRICE_DATE_INVALID: "合同航线的失效日期不能早于生效日期",
    SALES_CONTRACT_ROUTE_OVERLAP: "合同航线重叠：同一合同、同一起迄、同一机型、日期交叠的启用航线只能有一条",
    SALES_CONTRACT_ROUTE_PRICE_REQUIRED: "至少要有一档价",
    SALES_CONTRACT_ROUTE_CURRENCY_MISMATCH: "两档价必须是同一种币种",
    SALES_CONTRACT_ROUTE_TIER_EXISTS: "这一档已经存在，不覆盖先保存的单价",
    SALES_FUEL_TIER_OVERLAP: "燃油阶梯的偏差区间重叠",
    SALES_FUEL_TIER_RANGE_INVALID: "燃油阶梯的上限不能小于下限",
    SALES_ROUTE_PRICE_PINNED: "该航线价已被订单钉住，不能停用或修改；确需更正请走人工处理（停用旧行后新增一行）",
    NO_BILLABLE_LEGS: "没有还要算的航段，已停止出账",
    FLIGHT_LEGS_DISCONNECTED: "这趟航段接不上，已停止出账",
    SALES_ACMI_FUEL_SKIPPED: "这一版不算燃油",
    SALES_ACMI_RETURN_NOT_BILLABLE: "回程订单这一版出不了账",
    SALES_ACMI_TERMS_LOCKED: "已经出了包干，不能再改机号、起止日期或包干价",
    SALES_ACMI_ROUTE_REJECTED: "时段包机不按航线计价，不能写入航线价",
    SALES_ACMI_REGISTRATION_TOO_LONG: "机号不能超过 50 个字",
    CHARTER_BOOKING_NOT_CB: "回程订单这一版出不了账",
};

export const getErrorMessage = (error, defaultMessage = "操作失败") => {
    if (error?.response?.data) {
        if (typeof error.response.data === "string") {
            return SALES_ERROR_MESSAGE_MAP[error.response.data] || error.response.data;
        }
        const detail = error.response.data.detail;
        if (detail) {
            if (typeof detail === "string") {
                return SALES_ERROR_MESSAGE_MAP[detail] || detail;
            }
            if (typeof detail === "object") {
                // 结构化错误体：优先用后端给的可读说明（如被钉住的订单号），
                // 其次退回内层错误码（如 SALES_RECEIVABLE_MANUALLY_ADJUSTED）
                if (typeof detail.message === "string" && detail.message) {
                    return detail.message;
                }
                if (typeof detail.detail === "string") {
                    return SALES_ERROR_MESSAGE_MAP[detail.detail] || detail.detail;
                }
            }
        }
        if (error.response.data.message) {
            return error.response.data.message;
        }
    }
    if (error?.message) {
        return error.message;
    }
    return defaultMessage;
};

// 从 409 错误体里识别「存在手工调整应收」冲突，返回被锁定的应收清单；否则返回 null。
export const getManualAdjustedConflict = (error) => {
    const detail = error?.response?.data?.detail;
    if (error?.response?.status === 409 && detail && typeof detail === "object" && detail.detail === "SALES_RECEIVABLE_MANUALLY_ADJUSTED") {
        return Array.isArray(detail.manual_adjusted_receivables) ? detail.manual_adjusted_receivables : [];
    }
    return null;
};

export const renderManualAdjustedList = (list = [], action = "强制重算") => (
    <div>
        <p style={{ marginBottom: 8 }}>该订单存在以下手工调整应收，{action}会清除手工调整标记并按合同规则重新计算：</p>
        <ul style={{ paddingLeft: 18, marginBottom: 0, maxHeight: 200, overflowY: "auto" }}>
            {(list || []).map((r) => (
                <li key={r.receivable_id || r.receivable_no}>
                    {r.receivable_no}（{RECEIVABLE_TYPE_TEXT[r.type] || r.type}）当前 {formatMoney(r.amount)} {r.currency || ""}
                    {r.manual_adjust_reason ? ` — ${r.manual_adjust_reason}` : ""}
                </li>
            ))}
        </ul>
    </div>
);

export const serializeDate = (value) => (value ? dayjs(value).format("YYYY-MM-DD") : null);
export const serializeMonth = (value) => (value ? dayjs(value).format("YYYY-MM") : null);

export const generateRouteCode = (originAirport, destinationAirport) => {
    const origin = normalizeAirport(originAirport);
    const destination = normalizeAirport(destinationAirport);
    if (!origin || !destination) {
        return "";
    }
    return `${origin}-${destination}`;
};

export const getReceivableProgress = (record) => {
    const amount = Number(record?.amount || 0);
    const paidAmount = Number(record?.paid_amount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
        return {
            percent: record?.status === "PAID" ? 100 : 0,
            paidAmount,
            remainingAmount: 0,
        };
    }
    const percent = Math.max(0, Math.min(100, (paidAmount / amount) * 100));
    return {
        percent,
        paidAmount,
        remainingAmount: Math.max(amount - paidAmount, 0),
    };
};

export const getOutstandingAmount = (totalAmount, paidAmount) =>
    Math.max(Number(totalAmount || 0) - Number(paidAmount || 0), 0);

export const downloadCsv = (filename, rows) => {
    const escapeCell = (value) => {
        const text = value == null ? "" : String(value);
        if (/[",\n]/.test(text)) {
            return `"${text.replace(/"/g, "\"\"")}"`;
        }
        return text;
    };

    const csv = rows.map((row) => row.map(escapeCell).join(",")).join("\n");
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
};

export const getToneClass = (type, value) => {
    if (type === "route-status") {
        return value === "ACTIVE" ? "green" : "gray";
    }
    if (type === "route-source") {
        if (value === "SALES_CONTRACT") {
            return "blue";
        }
        if (value === "SCHEDULE") {
            return "cyan";
        }
        if (value === "HISTORICAL_FLIGHT") {
            return "gray";
        }
        return "orange";
    }
    if (type === "contract-status") {
        if (value === "ACTIVE") {
            return "green";
        }
        if (value === "DRAFT") {
            return "blue";
        }
        return "gray";
    }
    if (type === "credit-status") {
        if (value === "ACTIVE") {
            return "green";
        }
        if (value === "EXHAUSTED") {
            return "gold";
        }
        return "gray";
    }
    if (type === "receivable-status") {
        if (value === "PAID") {
            return "green";
        }
        if (value === "PARTIALLY_PAID") {
            return "gold";
        }
        if (value === "INVOICED") {
            return "orange";
        }
        if (value === "PENDING_INVOICE") {
            return "blue";
        }
        if (value === "EXCEPTION") {
            return "red";
        }
        return "gray";
    }
    if (type === "invoice-status") {
        if (value === "PAID") {
            return "green";
        }
        if (value === "PARTIALLY_PAID") {
            return "gold";
        }
        if (value === "ISSUED") {
            return "blue";
        }
        return "gray";
    }
    if (type === "batch-action") {
        if (["CREATED", "CREATE_PREVIEW"].includes(value)) {
            return "green";
        }
        if (value === "REUSED") {
            return "blue";
        }
        if (["EXCEPTION", "EXCEPTION_PREVIEW", "ERROR"].includes(value)) {
            return "red";
        }
        if (["SKIP_MISSING_CONTRACT", "SKIP_CONTRACT_MISMATCH"].includes(value)) {
            return "gold";
        }
        return "gray";
    }
    if (type === "fuel-preview-status") {
        if (value === "READY") {
            return "green";
        }
        if (value === "REUSED") {
            return "blue";
        }
        if (value === "ERROR") {
            return "red";
        }
        return "gray";
    }
    return "gray";
};

export const SectionTag = ({ tone, children }) => (
    <span className={`sales-revenue-tag sales-revenue-tag-${tone}`}>{children}</span>
);

let filterMeasureCanvas;

const filterMeasureFont = () => {
    if (typeof document === "undefined" || !document.body) {
        return "14px sans-serif";
    }
    const family = window.getComputedStyle(document.body).fontFamily || "sans-serif";
    return `14px ${family}`;
};

const measureFilterText = (text) => {
    const value = text == null ? "" : String(text);
    if (!value || typeof document === "undefined") {
        return value.length * 14;
    }
    if (!filterMeasureCanvas) {
        filterMeasureCanvas = document.createElement("canvas");
    }
    const context = filterMeasureCanvas.getContext("2d");
    if (!context) {
        return value.length * 14;
    }
    context.font = filterMeasureFont();
    return context.measureText(value).width;
};

export const measureFilterControlWidth = (labels, { withArrow = true, withClear = false } = {}) => {
    const longest = (labels || []).reduce((max, label) => Math.max(max, measureFilterText(label)), 0);
    const chrome = 24 + (withArrow ? 26 : 0) + (withClear ? 18 : 0) + 8;
    return Math.ceil(longest + chrome);
};
